import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  EventService,
  IdleService,
  MainHelperService,
} from '@services';
import {
  MotifButtonModule,
  MotifFormsModule,
  MotifHeaderModule,
} from '@ey-xd/ng-motif';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgOptimizedImage } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { APP_EVENT_KEY, THEME } from '@models';
import { UserProfileDropdownComponent } from '@components/user-profile-dropdown/user-profile-dropdown.component';
import { CompaniesDropdownComponent } from '@components/companies-dropdown/companies-dropdown.component';
import { FULL_HOME_ROUTE } from '@routes';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    MotifHeaderModule,
    NgOptimizedImage,
    TranslateModule,
    MotifButtonModule,
    MotifFormsModule,
    UserProfileDropdownComponent,
    CompaniesDropdownComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  public timeLeft: WritableSignal<number> = signal<number>(-1);
  public theme = signal<THEME>(THEME.LIGHT);
  public logoURL = signal<string>('');
  #idleService: IdleService = inject(IdleService);
  #destroyRef: DestroyRef = inject(DestroyRef);
  #eventService: EventService = inject(EventService);
  #mainHelperService: MainHelperService = inject(MainHelperService);
  #showMenu = false;
  fullHomeRoute = FULL_HOME_ROUTE;

  ngOnInit() {
    this.#initializeIdleService();
    this.#setupLogoURL();
    this.#subscribeToThemeToggle();
  }

  public toggleShowMenu(): void {
    this.#showMenu = !this.#showMenu;
    this.#eventService.emitEvent(APP_EVENT_KEY.TOGGLE_NAV_MENU, this.#showMenu);
  }

  #initializeIdleService(): void {
    this.#idleService
      .getTimeLeft()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(timeLeft => {
        this.timeLeft.set(timeLeft);
      });
  }

  #subscribeToThemeToggle(): void {
    this.#mainHelperService
      .subscribeToThemeToggle()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((theme: boolean): void => {
        this.theme.set(theme ? THEME.DARK : THEME.LIGHT);
        this.#setupLogoURL();
      });
  }

  #setupLogoURL() {
    this.logoURL.set(
      this.#mainHelperService.isDarkTheme()
        ? './assets/images/ey-dark-logo.svg'
        : './assets/images/ey-light-logo.svg'
    );
  }
}
